/**
 * The constructor: build on the left, the phone in the middle, the inspector
 * on the right (DESIGN §9.3).
 *
 * Three things here are load-bearing rather than decorative.
 *
 * The preview is the *same document* the phone renders, so what a customer
 * arranges is what installs. Every keystroke is written to a local draft, so
 * the one thing that must never happen — losing an afternoon of work to a
 * closed tab or a `409 design_stale` — cannot.
 *
 * And it is a *workspace*, not a document: the editor is exactly as tall as
 * the viewport leaves it, the two side panels scroll inside themselves, and
 * the stage scales the phone to fit what is left. Nothing about a screen
 * design should require scrolling a page to see the bottom of a screen.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiError, api } from "../api/client";
import type { ModuleOut } from "../api/types";
import { ErrorBox, Loading } from "../components/common";
import BlockInspector from "../design/BlockInspector";
import BlockList from "../design/BlockList";
import PhonePreview from "../design/PhonePreview";
import ThemePanel from "../design/ThemePanel";
import {
  BlockIcon,
  IconBack,
  IconDuplicate,
  IconInfo,
  IconMore,
  IconRedo,
  IconScreen,
  IconTrash,
  IconUndo,
} from "../design/icons";
import type { PaletteItem } from "../design/dnd";
import { useImageImport } from "../design/imageImport";
import {
  buildIndex,
  moduleBlockName,
  moduleName,
  referencedModules,
  sampleBlocks,
  sampleRows,
} from "../design/modules";
import { seedParams } from "../design/pickers";
import {
  IMAGE_TYPES,
  MODULE_BLOCK,
  errorsOf,
  issueBlockId,
  newBlock,
  newModuleBlock,
  settled,
  validateSpec,
  warningsOf,
  type Issue,
  type Spec,
} from "../design/spec";
import { clearDraft, readDraft, useDesignEditor } from "../design/useDesignEditor";
import { FALLBACK_SCALE, ZOOM_LEVELS, fitScale, zoomLabel } from "../design/zoom";
import { useI18n } from "../i18n";

/** ⌘ on a Mac, Ctrl everywhere else — for the tooltips, not for the handler,
 *  which honours both whatever the keyboard says. */
const MOD =
  typeof navigator !== "undefined" && /mac|iphone|ipad/i.test(navigator.platform || "")
    ? "⌘"
    : "Ctrl+";

export default function ScreenDesignEditor() {
  const { id = "" } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const design = useQuery({
    queryKey: ["screen-design", id],
    queryFn: () => api.screenDesign(id),
    enabled: !!id,
  });
  // Fetched once when the editor opens: the pickers, the preview's samples
  // and the validator all read the same catalogue (MODULES §8.3).
  const modules = useQuery({ queryKey: ["modules"], queryFn: api.modules });

  if (design.isLoading || !design.data) {
    return (
      <div className="stack">
        {design.error ? <ErrorBox error={design.error} /> : <Loading what={t("design.title")} />}
      </div>
    );
  }

  return (
    <Editor
      key={`${id}:${design.data.version}`}
      designId={id}
      initial={design.data.spec}
      name={design.data.name}
      version={design.data.version}
      isDefault={design.data.is_default}
      assets={design.data.assets}
      modules={modules.data}
      onReload={() => queryClient.invalidateQueries({ queryKey: ["screen-design", id] })}
      onLeave={() => navigate("/design")}
      onGenerate={() => navigate(`/agent-apk?design=${id}`)}
    />
  );
}

function Editor({
  designId,
  initial,
  name: savedName,
  version,
  isDefault,
  assets,
  modules,
  onReload,
  onLeave,
  onGenerate,
}: {
  designId: string;
  initial: Spec;
  name: string;
  version: number;
  isDefault: boolean;
  assets: import("../api/types").ScreenDesignAsset[];
  /** Undefined while `GET /modules` is in flight, or when it failed. */
  modules: ModuleOut[] | undefined;
  onReload: () => void;
  onLeave: () => void;
  onGenerate: () => void;
}) {
  const { t, lang } = useI18n();
  const queryClient = useQueryClient();
  const editor = useDesignEditor(designId, initial, version);
  const [name, setName] = useState(savedName);
  const [restorable, setRestorable] = useState(() => {
    const draft = readDraft(designId);
    // Only offer a draft that is *newer* than the row: an older one is work
    // that has already been saved, or someone else's stale tab.
    return draft && draft.baseVersion >= version ? draft : null;
  });

  // Awaited by the image import: the asset list the picker and the limit
  // count work from has to catch up with what was just uploaded.
  const refreshDesign = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["screen-designs"] });
    return queryClient.invalidateQueries({ queryKey: ["screen-design", designId] });
  }, [queryClient, designId]);
  const uploadAsset = useCallback(
    (file: File) => api.uploadScreenDesignAsset(designId, file),
    [designId],
  );
  // Drop, paste and the palette's *Image* tile all end up here (§9.3).
  const imports = useImageImport({
    assets,
    blocks: editor.spec.blocks,
    dispatch: editor.dispatch,
    upload: uploadAsset,
    onUploaded: refreshDesign,
  });

  /** Where a new block goes when the gesture did not name a slot: after what
   *  is selected, so a run of clicks builds a screen in the order you think. */
  const nextSlot = useCallback(() => {
    const index = editor.spec.blocks.findIndex((block) => block.id === editor.selectedId);
    return index < 0 ? editor.spec.blocks.length : index + 1;
  }, [editor.spec.blocks, editor.selectedId]);

  // The file dialog lives here rather than in the palette: a picture can be
  // asked for by a tile, by a tile dropped into a slot, or by one dropped on
  // the phone, and all three have to land in the same place.
  const files = useRef<HTMLInputElement>(null);
  const imageSlot = useRef<number | null>(null);
  const askForImage = useCallback((at: number) => {
    imageSlot.current = at;
    files.current?.click();
  }, []);
  const append = useCallback(
    (dropped: File[]) => imports.importFiles(dropped, { kind: "insert", index: nextSlot() }),
    [imports, nextSlot],
  );

  /** A palette tile, clicked or dropped. `at` is undefined for a click. */
  const addBlock = useCallback(
    (item: PaletteItem, at?: number) => {
      const index = at ?? nextSlot();
      if (item.kind === "module") {
        // Seeded from the manifest exactly as a picked action's params are, so
        // a required link starts at `https://` rather than empty (§2.11).
        const declared = modules?.find((entry) => entry.id === item.module)?.blocks?.[item.block];
        const block = newModuleBlock(item.module, item.block, seedParams(declared?.props));
        editor.dispatch({ type: "insertBlock", block, at: index });
        return;
      }
      // An image block with no image is a step nobody wants: the palette asks
      // for the file and the block comes with it.
      if (item.type === "image") askForImage(index);
      else editor.dispatch({ type: "insertBlock", block: newBlock(item.type), at: index });
    },
    [askForImage, editor.dispatch, modules, nextSlot],
  );

  // The design's images, plus the ones this editor uploaded that the refetch
  // has not brought back yet — a block must never name an asset the preview
  // cannot resolve, not even for one frame.
  const known = useMemo(() => {
    const listed = new Set(assets.map((asset) => asset.id));
    return [...assets, ...imports.uploaded.filter((asset) => !listed.has(asset.id))];
  }, [assets, imports.uploaded]);
  const assetIds = useMemo(() => new Set(known.map((asset) => asset.id)), [known]);
  const assetUrls = useMemo(
    () => Object.fromEntries(known.map((asset) => [asset.id, asset.url])),
    [known],
  );
  // No catalogue yet is not the same as an empty one: without it the module
  // bits are checked for shape only, so a slow request never tells a customer
  // their design is broken.
  const index = useMemo(() => (modules ? buildIndex(modules) : undefined), [modules]);
  // What the server would be sent: a picture still uploading is not part of
  // the document yet, so it is neither validated nor saved (§9.3).
  const spec = useMemo(() => settled(editor.spec), [editor.spec]);
  const issues = useMemo(() => validateSpec(spec, assetIds, index), [spec, assetIds, index]);
  const errors = useMemo(() => errorsOf(issues), [issues]);
  const warnings = useMemo(() => warningsOf(issues), [issues]);
  const samples = useMemo(() => sampleRows(modules ?? [], lang), [modules, lang]);
  const blockSamples = useMemo(() => sampleBlocks(modules ?? [], lang), [modules, lang]);
  // Which of the modules this design names are switched off — what the banner
  // above the frame explains once for all of them (MODULES §8.6).
  const disabled = useMemo(() => {
    const off = new Set<string>();
    for (const moduleId of referencedModules(editor.spec)) {
      const module = modules?.find((entry) => entry.id === moduleId);
      if (module && !module.enabled) off.add(moduleId);
    }
    return off;
  }, [editor.spec, modules]);

  const save = useMutation({
    mutationFn: () => api.updateScreenDesign(designId, { name: name.trim(), spec, version }),
    onSuccess: (saved) => {
      clearDraft(designId);
      editor.dispatch({ type: "saved", spec: saved.spec });
      refreshDesign();
    },
  });
  const setDefault = useMutation({
    mutationFn: () => api.setDefaultScreenDesign(designId),
    onSuccess: refreshDesign,
  });
  const upload = useMutation({
    mutationFn: (file: File) => api.uploadScreenDesignAsset(designId, file),
    onSuccess: refreshDesign,
  });
  const removeAsset = useMutation({
    mutationFn: (assetId: string) => api.deleteScreenDesignAsset(designId, assetId),
    onSuccess: refreshDesign,
  });

  const stale = save.error instanceof ApiError && save.error.code === "design_stale";
  // Errors only: a warning is saved as happily as a clean document, and the
  // *build* is where a switched-off module becomes a refusal (MODULES §7.3).
  const blocked = errors.length > 0;
  const dirty = editor.dirty || name !== savedName;
  // A picture still on its way is not in the document `settled()` would send,
  // and saving without it then dirtying the design a second later is not what
  // the customer asked for.
  const canSave = dirty && !blocked && !save.isPending && imports.pending === 0;

  /** The one line of the toolbar that says where the work stands. */
  const state: "saving" | "issues" | "unsaved" | "saved" = save.isPending
    ? "saving"
    : blocked
      ? "issues"
      : dirty
        ? "unsaved"
        : "saved";
  const stateText = {
    saving: t("design.saving"),
    issues: t("design.stateIssues", { n: errors.length }),
    unsaved: t("design.stateUnsaved"),
    saved: t("design.stateSaved"),
  }[state];

  /** Jump to the block an issue names, or to the screen when it names none. */
  const selectIssue = (path: string) => {
    const blockId = issueBlockId(path);
    editor.dispatch({
      type: "select",
      id: blockId && editor.spec.blocks.some((block) => block.id === blockId) ? blockId : null,
    });
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) return;
      const key = event.key.toLowerCase();
      if (key === "s") {
        event.preventDefault();
        if (canSave) save.mutate();
      } else if (key === "z") {
        // The document's history, not the focused field's: every keystroke in
        // the inspector is already a step in it, so one ⌘Z means one thing
        // wherever the cursor happens to be.
        event.preventDefault();
        editor.dispatch({ type: event.shiftKey ? "redo" : "undo" });
      } else if (key === "y") {
        event.preventDefault();
        editor.dispatch({ type: "redo" });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canSave, save, editor.dispatch]);

  // ⌘V with a screenshot on the clipboard is the same gesture as a drop, and
  // it is how most people already move one picture between two apps.
  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const pasted = Array.from(event.clipboardData?.files ?? []);
      if (!pasted.some((file) => file.type.startsWith("image/"))) return;
      event.preventDefault();
      // Any `image/*` goes through, so a pasted GIF is refused by name rather
      // than ignored without a word.
      append(pasted.filter((file) => file.type.startsWith("image/")));
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [append]);

  // A module block is called what its manifest calls it: the panel's title,
  // the tag on the frame and the list row all read the same word (§8.3).
  const selectedName = !editor.selected
    ? t("design.theme")
    : editor.selected.type === MODULE_BLOCK
      ? moduleBlockName(modules ?? [], editor.selected, lang)
      : t(`design.block.${editor.selected.type}`);

  return (
    <div className="design-editor">
      <input
        ref={files}
        type="file"
        accept={IMAGE_TYPES.join(",")}
        multiple
        hidden
        data-testid="design-image-input"
        onChange={(event) => {
          const chosen = Array.from(event.target.files ?? []);
          const at = imageSlot.current ?? editor.spec.blocks.length;
          imageSlot.current = null;
          event.target.value = "";
          if (chosen.length > 0) imports.importFiles(chosen, { kind: "insert", index: at });
        }}
      />

      <header className="design-toolbar">
        <button
          type="button"
          className="icon-btn"
          title={t("design.back")}
          aria-label={t("design.back")}
          onClick={onLeave}
        >
          <IconBack />
        </button>
        <div className="design-title">
          <input
            className="design-name"
            value={name}
            maxLength={128}
            aria-label={t("design.name")}
            onChange={(event) => setName(event.target.value)}
          />
          {isDefault && <span className="design-default-badge">{t("design.default")}</span>}
        </div>

        <div className="design-toolbar-actions">
          <div className="design-history">
            <button
              type="button"
              className="icon-btn"
              disabled={!editor.canUndo}
              title={`${t("design.undo")} (${MOD}Z)`}
              aria-label={t("design.undo")}
              onClick={() => editor.dispatch({ type: "undo" })}
            >
              <IconUndo />
            </button>
            <button
              type="button"
              className="icon-btn"
              disabled={!editor.canRedo}
              title={`${t("design.redo")} (${MOD}⇧Z)`}
              aria-label={t("design.redo")}
              onClick={() => editor.dispatch({ type: "redo" })}
            >
              <IconRedo />
            </button>
          </div>

          <span className="design-state" data-state={state} role="status">
            {stateText}
          </span>

          <button
            className="primary"
            type="button"
            disabled={!canSave}
            title={`${t("design.save")} (${MOD}S)`}
            onClick={() => save.mutate()}
          >
            {t("design.save")}
          </button>
          {/* The next step of the journey, and deliberately quieter than Save:
              a build of unsaved work is the wrong thing to make easy. */}
          <button type="button" className="design-next" onClick={onGenerate}>
            {t("design.buildApk")}
            <span aria-hidden="true">→</span>
          </button>

          <OverflowMenu label={t("design.more")}>
            {(close) => (
              <button
                type="button"
                role="menuitem"
                disabled={isDefault || setDefault.isPending}
                onClick={() => {
                  setDefault.mutate();
                  close();
                }}
              >
                {t("design.setDefault")}
              </button>
            )}
          </OverflowMenu>
        </div>
      </header>

      {restorable && (
        <section className="card design-banner design-restore">
          <p>{t("design.restorePrompt")}</p>
          <div className="row">
            <button
              className="primary"
              type="button"
              onClick={() => {
                editor.dispatch({ type: "reset", spec: restorable.spec, dirty: true });
                setRestorable(null);
              }}
            >
              {t("design.restore")}
            </button>
            <button
              type="button"
              onClick={() => {
                clearDraft(designId);
                setRestorable(null);
              }}
            >
              {t("design.discardDraft")}
            </button>
          </div>
        </section>
      )}

      {stale && (
        <section className="card design-banner design-stale">
          <p className="error-text">{t("design.staleMessage")}</p>
          <button type="button" onClick={onReload}>
            {t("design.reload")}
          </button>
        </section>
      )}
      {save.error && !stale && (
        <div className="design-banner design-banner-error">
          <ErrorBox error={save.error} />
        </div>
      )}
      {setDefault.error && (
        <div className="design-banner design-banner-error">
          <ErrorBox error={setDefault.error} />
        </div>
      )}
      {removeAsset.error && (
        <div className="design-banner design-banner-error">
          <ErrorBox error={removeAsset.error} />
        </div>
      )}
      {/* A refused file or a failed upload: the block is already gone, so the
          reason has to be here rather than where it would have been. */}
      {imports.error ? (
        <div className="design-banner design-banner-error">
          <ErrorBox error={imports.error} />
        </div>
      ) : null}

      <div className="design-workspace">
        <aside className="card design-panel">
          <div className="design-panel-body">
            <BlockList
              blocks={editor.spec.blocks}
              selectedId={editor.selectedId}
              onSelect={(blockId) => editor.dispatch({ type: "select", id: blockId })}
              onSelectScreen={() => editor.dispatch({ type: "select", id: null })}
              onAdd={addBlock}
              onMove={(blockId, delta) => editor.dispatch({ type: "moveBlock", id: blockId, delta })}
              onReorder={(blockId, to) => editor.dispatch({ type: "reorderBlock", id: blockId, to })}
              onImportFiles={append}
              onDuplicate={(blockId) => editor.dispatch({ type: "duplicateBlock", id: blockId })}
              onRemove={(blockId) => editor.dispatch({ type: "removeBlock", id: blockId })}
              modules={modules ?? []}
            />
          </div>
        </aside>

        <Stage
          banner={
            disabled.size > 0 ? (
              /* One banner for every issue a switched-off module causes — it is
                 the same fact repeated, and the fix is one link away (§8.6). */
              <p className="design-module-banner">
                {t("design.moduleDisabledBanner", {
                  modules: [...disabled]
                    .map((moduleId) => moduleName(modules ?? [], moduleId, lang))
                    .join(", "),
                })}{" "}
                <Link to="/modules">{t("design.openModules")}</Link>
              </p>
            ) : null
          }
          onDelete={
            editor.selectedId
              ? () => editor.dispatch({ type: "removeBlock", id: editor.selectedId! })
              : undefined
          }
        >
          {(scale) => (
            <PhonePreview
              spec={editor.spec}
              assets={assetUrls}
              scale={scale}
              sampleRows={samples}
              sampleBlocks={blockSamples}
              disabledModules={disabled}
              selectedId={editor.selectedId}
              selectedLabel={editor.selected ? selectedName : undefined}
              onSelect={(blockId) => editor.dispatch({ type: "select", id: blockId })}
              onSelectScreen={() => editor.dispatch({ type: "select", id: null })}
              onDropFiles={imports.importFiles}
              onReorder={(blockId, to) => editor.dispatch({ type: "reorderBlock", id: blockId, to })}
              onAddBlock={(item, at) => addBlock(item, at)}
              dropLabel={t("design.dropImage")}
              emptyLabel={t("design.emptyFrame")}
            />
          )}
        </Stage>

        <aside className="card design-panel design-inspector">
          <header className="design-panel-head">
            <span className="design-panel-icon">
              {editor.selected ? (
                <BlockIcon type={editor.selected.type} size={16} />
              ) : (
                <IconScreen size={16} />
              )}
            </span>
            <h3>{selectedName}</h3>
            {editor.selected && (
              <span className="design-panel-actions">
                <button
                  type="button"
                  className="icon-btn"
                  title={t("design.duplicate")}
                  aria-label={t("design.duplicate")}
                  onClick={() =>
                    editor.dispatch({ type: "duplicateBlock", id: editor.selected!.id })
                  }
                >
                  <IconDuplicate size={16} />
                </button>
                <button
                  type="button"
                  className="icon-btn danger"
                  title={t("design.remove")}
                  aria-label={t("design.remove")}
                  onClick={() => editor.dispatch({ type: "removeBlock", id: editor.selected!.id })}
                >
                  <IconTrash size={16} />
                </button>
              </span>
            )}
          </header>

          <div className="design-panel-body">
            {blocked && (
              <div className="design-issues">
                <h4 className="error-text">{t("design.issues")}</h4>
                <IssueList issues={errors} onSelect={selectIssue} />
              </div>
            )}
            {warnings.length > 0 && (
              /* Under the errors, never in front of Save: these are things a
                 customer may keep and fix later (MODULES §8.6). */
              <div className="design-issues design-warnings">
                <h4>{t("design.warnings")}</h4>
                <IssueList issues={warnings} onSelect={selectIssue} />
              </div>
            )}

            {editor.selected ? (
              <BlockInspector
                block={editor.selected}
                assets={known}
                modules={modules ?? []}
                uploading={upload.isPending}
                uploadError={upload.error}
                onPatch={(patch) =>
                  editor.dispatch({ type: "updateBlock", id: editor.selected!.id, patch })
                }
                onUpload={(file) => upload.mutate(file)}
                onDeleteAsset={(assetId) => removeAsset.mutate(assetId)}
              />
            ) : (
              <ThemePanel
                theme={editor.spec.theme ?? {}}
                assets={known}
                uploading={upload.isPending}
                uploadError={upload.error}
                onChange={(theme) => editor.dispatch({ type: "setTheme", theme })}
                onUpload={(file) => upload.mutate(file)}
                onDeleteAsset={(assetId) => removeAsset.mutate(assetId)}
              />
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

/**
 * The canvas: it measures itself and hands the phone the scale that fits.
 *
 * *Fit* is the default because the frame is 800 dp tall and a laptop has about
 * 560 px to give it — at 1:1 the bottom of the customer's own screen would be
 * below the fold of a page that must not scroll. The fixed steps are there for
 * when a colour or a corner radius has to be judged at true size, and 100 % is
 * the one case where this box scrolls.
 */
function Stage({
  banner,
  onDelete,
  children,
}: {
  banner: ReactNode;
  /** Delete/Backspace with a block selected, when the focus is on the canvas. */
  onDelete?: () => void;
  children: (scale: number) => ReactNode;
}) {
  const { t } = useI18n();
  const canvas = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ width: number; height: number } | null>(null);
  const [zoom, setZoom] = useState<"fit" | number>("fit");

  useEffect(() => {
    const node = canvas.current;
    // jsdom has no ResizeObserver, and neither has an ancient browser: both
    // simply keep the fallback scale, which is a phone at a sensible size.
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[entries.length - 1]?.contentRect;
      if (rect && rect.width > 0 && rect.height > 0) {
        setBox({ width: rect.width, height: rect.height });
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const fitted = box ? fitScale(box.width, box.height) : FALLBACK_SCALE;
  const scale = zoom === "fit" ? fitted : zoom;

  return (
    <section
      className="design-stage"
      tabIndex={-1}
      onMouseDown={(event) => {
        // Focus follows the click, so Delete has somewhere to be heard. A
        // control inside takes it back on its own default action.
        event.currentTarget.focus();
      }}
      onKeyDown={(event) => {
        if (event.key !== "Delete" && event.key !== "Backspace") return;
        // Never while something is being typed into: Backspace is a letter.
        const target = event.target as HTMLElement | null;
        if (target?.closest("input, textarea, select, [contenteditable='true']")) return;
        if (!onDelete) return;
        event.preventDefault();
        onDelete();
      }}
    >
      {banner}
      <div className="design-canvas" ref={canvas}>
        {children(scale)}
      </div>

      <div className="design-zoom">
        <div className="toolgroup seg" role="group" aria-label={t("design.zoom")}>
          <button
            type="button"
            className={zoom === "fit" ? "on" : undefined}
            aria-pressed={zoom === "fit"}
            onClick={() => setZoom("fit")}
          >
            {t("design.zoomFit")}
          </button>
          {ZOOM_LEVELS.map((level) => (
            <button
              key={level}
              type="button"
              className={zoom === level ? "on" : undefined}
              aria-pressed={zoom === level}
              onClick={() => setZoom(level)}
            >
              {zoomLabel(level)}
            </button>
          ))}
        </div>
        <span className="design-zoom-value">{zoomLabel(scale)}</span>
        {/* The footnote that used to be a paragraph under the phone. It is a
            caveat, not an instruction, so it waits to be asked. */}
        <span
          className="design-note"
          role="img"
          tabIndex={0}
          title={t("design.previewNote")}
          aria-label={t("design.previewNote")}
        >
          <IconInfo size={15} />
        </span>
      </div>
    </section>
  );
}

/** Errors and warnings read the same and click the same; only the heading differs. */
function IssueList({ issues, onSelect }: { issues: Issue[]; onSelect: (path: string) => void }) {
  return (
    <ul>
      {issues.map((issue, index) => (
        <li key={index}>
          <button type="button" className="design-issue" onClick={() => onSelect(issue.path)}>
            {issue.message}
            {issue.path && <span className="mono small"> {issue.path}</span>}
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * The ⋯ button and the little sheet under it.
 *
 * Everything a customer does once per design — making it the default — lives
 * here, so the toolbar can be read left to right as "this is the design, this
 * is its state, save it, build it".
 */
function OverflowMenu({
  label,
  children,
}: {
  label: string;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: MouseEvent) => {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="design-overflow" ref={wrap}>
      <button
        type="button"
        className="icon-btn"
        title={label}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        <IconMore />
      </button>
      {open && (
        <div className="design-menu" role="menu">
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}
